import {
    createContext,
    type Dispatch,
    lazy,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
    type RefObject,
    type SetStateAction,
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useT } from "@magic-md/i18n/react";
import { Pencil, Reply, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { getDocumentBundleDir } from "../../lib/publish-utils";
import { useEditorStore } from "../../store/editor-store";
import type { CommentComposerDraft } from "./comment-composer-model";
import {
    buildAnnotationCommentTree,
    deleteAnnotationComment,
    getAnnotationComments,
    hasCommentDraftContent,
    type AnnotationCommentThreadNode,
    updateAnnotationComment,
} from "./comments";
import {
    ANNOTATION_BLOCK_COMMENT_EVENT,
    ANNOTATION_REBIND_EVENT,
    ANNOTATION_SELECT_EVENT,
    type AnnotationBlockCommentDetail,
    type AnnotationRebindDetail,
    type AnnotationSelectDetail,
} from "./events";
import {
    getAnnotationDir,
    loadAnnotationDocument,
    saveAnnotationDocument,
} from "./persistence";
import { getAnnotationFloatingStyle } from "./positioning";
import { relocateSourceTextAnnotations } from "./relocation";
import {
    findRenderedTextAnnotationAtPoint,
    readRenderedTextSelection,
} from "./rendered-text";
import {
    findRenderedBlockRootByBlockId,
    getRenderedTextAnnotationRect,
    relocateRenderedAnnotations,
} from "./rendered-relocation";
import { getSelectionContext, offsetToSourceRange } from "./source";
import type {
    AnnotationComment,
    AnnotationCommentReference,
    AnnotationStyleKind,
    PreviewAnnotation,
} from "./types";

const BUNDLE_REFRESH_EVENT = "magic-md:bundle-refresh";
const EMPTY_PREVIEW_ANNOTATIONS: PreviewAnnotation[] = [];
const CommentComposer = lazy(async () => {
    const module = await import("./CommentComposer");
    return { default: module.CommentComposer };
});

interface RenderedAnnotationIndex {
    block: Map<string, PreviewAnnotation[]>;
    area: Map<string, PreviewAnnotation[]>;
}

export interface AnnotationContextValue {
    documentPath: string;
    annotations: PreviewAnnotation[];
    reload: () => Promise<void>;
    areaPlacement: RenderedAreaPlacement | null;
    elementPlacement: RenderedElementPlacement | null;
    startRenderedAreaPlacement: (placement: RenderedAreaPlacement) => void;
    startRenderedElementPlacement: (placement: RenderedElementPlacement) => void;
    cancelRenderedAreaPlacement: () => void;
    cancelRenderedElementPlacement: () => void;
    selectRenderedArea: (selection: PendingAreaComment) => void;
    selectRenderedElement: (selection: PendingElementComment) => void;
    getRenderedBlockAnnotations: (blockId: string) => PreviewAnnotation[];
    getRenderedAreaAnnotations: (blockId: string) => PreviewAnnotation[];
}

type PendingSelection =
    | {
        kind: "source-text";
        startOffset: number;
        endOffset: number;
        quote: string;
        rect: DOMRect;
    }
    | {
        kind: "rendered-text";
        blockId: string;
        outputHash?: string;
        startOffset: number;
        endOffset: number;
        quote: string;
        prefix?: string;
        suffix?: string;
        rect: DOMRect;
    };

interface ActiveMark {
    id: string;
    rect: DOMRect;
}

interface PendingBlockComment {
    blockId: string;
    label?: string;
    outputHash?: string;
    rect: {
        left: number;
        top: number;
        bottom: number;
    };
}

interface RenderedAreaPlacement {
    blockId: string;
    label?: string;
    outputHash?: string;
}

interface RenderedElementPlacement {
    blockId: string;
    label?: string;
    outputHash?: string;
}

interface PendingAreaComment extends RenderedAreaPlacement {
    shape: "point" | "area";
    x: number;
    y: number;
    width: number;
    height: number;
    rect: {
        left: number;
        top: number;
        bottom: number;
    };
}

interface PendingElementComment extends RenderedElementPlacement {
    selector: string;
    elementLabel?: string;
    tagName?: string;
    rect: {
        left: number;
        top: number;
        bottom: number;
    };
}

const AnnotationContext = createContext<AnnotationContextValue | null>(null);

export function useAnnotationContextOptional(): AnnotationContextValue | null {
    return useContext(AnnotationContext);
}

export function AnnotationProvider({
    documentPath,
    content,
    children,
}: {
    documentPath?: string | null;
    content: string;
    children: ReactNode;
}) {
    const t = useT("editor");
    const rootRef = useRef<HTMLDivElement>(null);
    const [annotations, setAnnotations] = useState<PreviewAnnotation[]>([]);
    const [pending, setPending] = useState<PendingSelection | null>(null);
    const [activeMark, setActiveMark] = useState<ActiveMark | null>(null);
    const [commentOpen, setCommentOpen] = useState(false);
    const [pendingBlock, setPendingBlock] = useState<PendingBlockComment | null>(null);
    const [areaPlacement, setAreaPlacement] = useState<RenderedAreaPlacement | null>(null);
    const [pendingArea, setPendingArea] = useState<PendingAreaComment | null>(null);
    const [elementPlacement, setElementPlacement] = useState<RenderedElementPlacement | null>(null);
    const [pendingElement, setPendingElement] = useState<PendingElementComment | null>(null);
    const [rebindingAnnotationId, setRebindingAnnotationId] = useState<string | null>(null);
    const [replyTargetId, setReplyTargetId] = useState<string | null>(null);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [recentCommentId, setRecentCommentId] = useState<string | null>(null);
    const [replyComposerKey, setReplyComposerKey] = useState(0);
    const contentRef = useRef(content);
    const editorTabs = useEditorStore((state) => state.tabs);
    const activeTabId = useEditorStore((state) => state.activeTabId);
    const activeParsedDocument = useEditorStore((state) => state.parsedDocument);
    const parsedDocumentMap = useEditorStore((state) => state.parsedDocumentMap);
    const tabs = useMemo(
        () =>
            editorTabs.map((tab) => ({
                id: tab.id,
                title: tab.title,
                filePath: tab.filePath,
            })),
        [editorTabs],
    );
    const definitions = useMemo(() => {
        if (!documentPath) return [];
        const tab = editorTabs.find((item) => item.filePath === documentPath);
        const parsed = tab
            ? parsedDocumentMap.get(tab.id) ??
                (tab.id === activeTabId ? activeParsedDocument : null)
            : null;
        return [...(parsed?.definitions.values() ?? [])].map((definition) => ({
            id: definition.id,
            pluginName: definition.pluginName,
            hasOutput: Boolean(definition.output),
        }));
    }, [
        activeParsedDocument,
        activeTabId,
        documentPath,
        editorTabs,
        parsedDocumentMap,
    ]);

    const closeTransientUi = useCallback(() => {
        setPending(null);
        setCommentOpen(false);
        setPendingBlock(null);
        setPendingArea(null);
        setAreaPlacement(null);
        setPendingElement(null);
        setElementPlacement(null);
        setActiveMark(null);
        setReplyTargetId(null);
        setEditingCommentId(null);
        setRecentCommentId(null);
        setRebindingAnnotationId(null);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            closeTransientUi();
        };
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (target.closest(".magic-md-annotation-popover")) return;
            const root = rootRef.current;
            if (root?.contains(target)) return;
            closeTransientUi();
        };
        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("pointerdown", handlePointerDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [closeTransientUi]);

    useEffect(() => {
        contentRef.current = content;
    }, [content]);

    const persist = useCallback(
        async (nextAnnotations: PreviewAnnotation[]) => {
            if (!documentPath) return;
            try {
                await saveAnnotationDocument(documentPath, nextAnnotations);
                window.dispatchEvent(
                    new CustomEvent(BUNDLE_REFRESH_EVENT, {
                        detail: {
                            filePath: documentPath,
                            bundleDir: getDocumentBundleDir(documentPath),
                            annotationDir: getAnnotationDir(documentPath),
                        },
                    }),
                );
            } catch (error) {
                const message = error instanceof Error ? error.message : t("common.unknown");
                useEditorStore.getState().setStatusMessage(
                    t("annotation.status.persistFailed", { message }),
                );
            }
        },
        [documentPath, t],
    );

    const reload = useCallback(async () => {
        if (!documentPath) {
            setAnnotations([]);
            return;
        }
        const document = await loadAnnotationDocument(documentPath);
        const relocated = relocateSourceTextAnnotations({
            documentPath,
            content: contentRef.current,
            annotations: document.annotations,
        });
        setAnnotations(relocated.annotations);
        if (relocated.changed) {
            await persist(relocated.annotations);
        }
    }, [documentPath, persist]);

    useEffect(() => {
        let cancelled = false;
        if (!documentPath) {
            setAnnotations([]);
            return;
        }
        loadAnnotationDocument(documentPath)
            .then(async (document) => {
                const relocated = relocateSourceTextAnnotations({
                    documentPath,
                    content: contentRef.current,
                    annotations: document.annotations,
                });
                if (cancelled) return;
                setAnnotations(relocated.annotations);
                if (relocated.changed) {
                    await persist(relocated.annotations);
                }
            })
            .catch(() => {
                if (!cancelled) setAnnotations([]);
            });
        return () => {
            cancelled = true;
        };
    }, [documentPath, persist]);

    useEffect(() => {
        if (!documentPath || annotations.length === 0) return;
        const relocated = relocateSourceTextAnnotations({
            documentPath,
            content,
            annotations,
        });
        if (!relocated.changed) return;
        setAnnotations(relocated.annotations);
        void persist(relocated.annotations);
    }, [annotations, content, documentPath, persist]);

    useEffect(() => {
        const root = rootRef.current;
        if (!documentPath || !root || annotations.length === 0) return;
        const relocated = relocateRenderedAnnotations({
            documentPath,
            root,
            annotations,
        });
        if (!relocated.changed) return;
        setAnnotations(relocated.annotations);
        void persist(relocated.annotations);
    });

    const createAnnotation = useCallback(
        (kind: AnnotationStyleKind, draft?: CommentComposerDraft) => {
            if (!documentPath || !pending || pending.endOffset <= pending.startOffset) {
                return;
            }
            const now = new Date().toISOString();
            const context = pending.kind === "source-text"
                ? getSelectionContext(content, pending.startOffset, pending.endOffset)
                : {
                    quote: pending.quote,
                    prefix: pending.prefix,
                    suffix: pending.suffix,
                };
            const annotation: PreviewAnnotation = {
                id: createAnnotationId(),
                status: "open",
                style: {
                    kind,
                    color: kind === "comment" ? "blue" : "yellow",
                },
                target: pending.kind === "source-text"
                    ? {
                        kind: "source-text",
                        documentPath,
                        range: offsetToSourceRange(
                            content,
                            pending.startOffset,
                            pending.endOffset,
                        ),
                        startOffset: pending.startOffset,
                        endOffset: pending.endOffset,
                        quote: context.quote || pending.quote,
                        prefix: context.prefix,
                        suffix: context.suffix,
                    }
                    : {
                        kind: "rendered-text",
                        documentPath,
                        blockId: pending.blockId,
                        outputHash: pending.outputHash,
                        startOffset: pending.startOffset,
                        endOffset: pending.endOffset,
                        quote: context.quote || pending.quote,
                        prefix: context.prefix,
                        suffix: context.suffix,
                    },
                createdAt: now,
                updatedAt: now,
                thread: draft && hasCommentDraftContent(draft)
                    ? {
                        text: draft.text,
                        document: draft.document,
                        references: draft.references,
                        comments: [createThreadComment(draft, now)],
                    }
                    : undefined,
            };
            updateAnnotations(setAnnotations, persist, (current) => [...current, annotation]);
            setPending(null);
            setActiveMark(null);
            setCommentOpen(false);
            clearSelection(rootRef);
        },
        [content, documentPath, pending, persist],
    );

    const activeAnnotation = activeMark
        ? annotations.find((annotation) => annotation.id === activeMark.id)
        : null;
    const activeComments = activeAnnotation ? getAnnotationComments(activeAnnotation) : [];
    const activeCommentTree = buildAnnotationCommentTree(activeComments);

    const resolveAnnotation = useCallback((id: string) => {
        updateAnnotations(setAnnotations, persist, (current) =>
            current.map((annotation) =>
                annotation.id === id
                    ? {
                        ...annotation,
                        status: "resolved",
                        updatedAt: new Date().toISOString(),
                    }
                    : annotation,
            ),
        );
        setActiveMark(null);
    }, [persist]);

    const deleteAnnotation = useCallback((id: string) => {
        updateAnnotations(setAnnotations, persist, (current) =>
            current.filter((annotation) => annotation.id !== id),
        );
        setActiveMark(null);
    }, [persist]);

    const appendComment = useCallback((
        annotationId: string,
        draft: CommentComposerDraft,
        parentId?: string | null,
    ) => {
        if (!hasCommentDraftContent(draft)) return;
        const now = new Date().toISOString();
        const comment = createThreadComment(draft, now, parentId ?? undefined);
        updateAnnotations(setAnnotations, persist, (current) =>
            current.map((annotation) =>
                annotation.id === annotationId
                    ? {
                        ...annotation,
                        updatedAt: now,
                        thread: {
                            ...annotation.thread,
                            comments: [
                                ...getAnnotationComments(annotation),
                                comment,
                            ],
                        },
                    }
                    : annotation,
            ),
        );
        setReplyTargetId(null);
        setEditingCommentId(null);
        setRecentCommentId(comment.id);
        setReplyComposerKey((key) => key + 1);
        useEditorStore.getState().setStatusMessage(
            parentId ? t("annotation.status.replyAdded") : t("annotation.status.commentAdded"),
        );
    }, [persist, t]);

    const editComment = useCallback((
        annotationId: string,
        commentId: string,
        draft: CommentComposerDraft,
    ) => {
        if (!hasCommentDraftContent(draft)) return;
        const now = new Date().toISOString();
        updateAnnotations(setAnnotations, persist, (current) =>
            current.map((annotation) =>
                annotation.id === annotationId
                    ? updateAnnotationComment(annotation, commentId, draft, now)
                    : annotation,
            ),
        );
        setEditingCommentId(null);
        setRecentCommentId(commentId);
        setReplyComposerKey((key) => key + 1);
        useEditorStore.getState().setStatusMessage(t("annotation.status.commentUpdated"));
    }, [persist, t]);

    const deleteComment = useCallback((
        annotationId: string,
        commentId: string,
    ) => {
        const now = new Date().toISOString();
        updateAnnotations(setAnnotations, persist, (current) =>
            current.map((annotation) =>
                annotation.id === annotationId
                    ? deleteAnnotationComment(annotation, commentId, now)
                    : annotation,
            ),
        );
        setReplyTargetId(null);
        setEditingCommentId(null);
        setRecentCommentId(null);
        useEditorStore.getState().setStatusMessage(t("annotation.status.commentDeleted"));
    }, [persist, t]);

    const createBlockAnnotation = useCallback((draft: CommentComposerDraft) => {
        if (!documentPath || !pendingBlock || !hasCommentDraftContent(draft)) return;
        const now = new Date().toISOString();
        const annotation: PreviewAnnotation = {
            id: createAnnotationId(),
            status: "open",
            style: {
                kind: "comment",
                color: "blue",
            },
            target: {
                kind: "rendered-block",
                documentPath,
                blockId: pendingBlock.blockId,
                outputHash: pendingBlock.outputHash,
            },
            createdAt: now,
            updatedAt: now,
            thread: {
                text: draft.text,
                document: draft.document,
                references: draft.references,
                comments: [createThreadComment(draft, now)],
            },
        };
        updateAnnotations(setAnnotations, persist, (current) => [...current, annotation]);
        setPendingBlock(null);
    }, [documentPath, pendingBlock, persist]);

    const startRenderedAreaPlacement = useCallback((placement: RenderedAreaPlacement) => {
        setPending(null);
        setCommentOpen(false);
        setPendingBlock(null);
        setPendingArea(null);
        setPendingElement(null);
        setActiveMark(null);
        setReplyTargetId(null);
        setEditingCommentId(null);
        setElementPlacement(null);
        setAreaPlacement((current) =>
            current?.blockId === placement.blockId ? null : placement,
        );
    }, []);

    const cancelRenderedAreaPlacement = useCallback(() => {
        setAreaPlacement(null);
    }, []);

    const startRenderedElementPlacement = useCallback((placement: RenderedElementPlacement) => {
        setPending(null);
        setCommentOpen(false);
        setPendingBlock(null);
        setPendingArea(null);
        setPendingElement(null);
        setActiveMark(null);
        setReplyTargetId(null);
        setEditingCommentId(null);
        setAreaPlacement(null);
        setElementPlacement((current) =>
            current?.blockId === placement.blockId ? null : placement,
        );
    }, []);

    const cancelRenderedElementPlacement = useCallback(() => {
        setElementPlacement(null);
    }, []);

    const selectRenderedArea = useCallback((selection: PendingAreaComment) => {
        setPending(null);
        setCommentOpen(false);
        setPendingBlock(null);
        setActiveMark(null);
        setReplyTargetId(null);
        setEditingCommentId(null);
        setAreaPlacement(null);
        setPendingArea(selection);
    }, []);

    const selectRenderedElement = useCallback((selection: PendingElementComment) => {
        setPending(null);
        setCommentOpen(false);
        setPendingBlock(null);
        setPendingArea(null);
        setActiveMark(null);
        setReplyTargetId(null);
        setEditingCommentId(null);
        setAreaPlacement(null);
        setElementPlacement(null);
        setPendingElement(selection);
    }, []);

    const createAreaAnnotation = useCallback((draft: CommentComposerDraft) => {
        if (!documentPath || !pendingArea || !hasCommentDraftContent(draft)) return;
        const now = new Date().toISOString();
        const annotation: PreviewAnnotation = {
            id: createAnnotationId(),
            status: "open",
            style: {
                kind: "comment",
                color: "blue",
            },
            target: {
                kind: "rendered-area",
                documentPath,
                blockId: pendingArea.blockId,
                outputHash: pendingArea.outputHash,
                shape: pendingArea.shape,
                x: pendingArea.x,
                y: pendingArea.y,
                width: pendingArea.width,
                height: pendingArea.height,
            },
            createdAt: now,
            updatedAt: now,
            thread: {
                text: draft.text,
                document: draft.document,
                references: draft.references,
                comments: [createThreadComment(draft, now)],
            },
        };
        updateAnnotations(setAnnotations, persist, (current) => [...current, annotation]);
        setPendingArea(null);
    }, [documentPath, pendingArea, persist]);

    const createElementAnnotation = useCallback((draft: CommentComposerDraft) => {
        if (!documentPath || !pendingElement || !hasCommentDraftContent(draft)) return;
        const now = new Date().toISOString();
        const annotation: PreviewAnnotation = {
            id: createAnnotationId(),
            status: "open",
            style: {
                kind: "comment",
                color: "blue",
            },
            target: {
                kind: "rendered-element",
                documentPath,
                blockId: pendingElement.blockId,
                outputHash: pendingElement.outputHash,
                selector: pendingElement.selector,
                label: pendingElement.elementLabel,
                tagName: pendingElement.tagName,
            },
            createdAt: now,
            updatedAt: now,
            thread: {
                text: draft.text,
                document: draft.document,
                references: draft.references,
                comments: [createThreadComment(draft, now)],
            },
        };
        updateAnnotations(setAnnotations, persist, (current) => [...current, annotation]);
        setPendingElement(null);
    }, [documentPath, pendingElement, persist]);

    const renderedAnnotationIndex = useMemo(
        () => buildRenderedAnnotationIndex(annotations, documentPath),
        [annotations, documentPath],
    );
    const getRenderedBlockAnnotations = useCallback(
        (blockId: string) =>
            renderedAnnotationIndex.block.get(blockId) ?? EMPTY_PREVIEW_ANNOTATIONS,
        [renderedAnnotationIndex],
    );
    const getRenderedAreaAnnotations = useCallback(
        (blockId: string) =>
            renderedAnnotationIndex.area.get(blockId) ?? EMPTY_PREVIEW_ANNOTATIONS,
        [renderedAnnotationIndex],
    );

    const value = useMemo<AnnotationContextValue | null>(() => {
        if (!documentPath) return null;
        return {
            documentPath,
            annotations,
            reload,
            areaPlacement,
            elementPlacement,
            startRenderedAreaPlacement,
            startRenderedElementPlacement,
            cancelRenderedAreaPlacement,
            cancelRenderedElementPlacement,
            selectRenderedArea,
            selectRenderedElement,
            getRenderedBlockAnnotations,
            getRenderedAreaAnnotations,
        };
    }, [
        annotations,
        areaPlacement,
        elementPlacement,
        cancelRenderedAreaPlacement,
        cancelRenderedElementPlacement,
        documentPath,
        getRenderedAreaAnnotations,
        getRenderedBlockAnnotations,
        reload,
        selectRenderedArea,
        selectRenderedElement,
        startRenderedAreaPlacement,
        startRenderedElementPlacement,
    ]);

    useEffect(() => {
        if (!documentPath) return;
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<AnnotationSelectDetail>).detail;
            if (!detail || detail.documentPath !== documentPath) return;
            const mark = rootRef.current?.querySelector(
                `[data-md-annotation-ids~="${cssAttributeValue(detail.annotationId)}"]`,
            ) as HTMLElement | null;
            if (!mark) {
                const annotation = annotations.find((item) => item.id === detail.annotationId);
                const root = rootRef.current;
                if (annotation?.target.kind === "rendered-text" && root) {
                    const rect = getRenderedTextAnnotationRect(root, annotation.target);
                    if (rect) {
                        setActiveMark({
                            id: detail.annotationId,
                            rect,
                        });
                        return;
                    }
                }
                const blockId = annotation?.target.kind === "rendered-block" ||
                    annotation?.target.kind === "rendered-area" ||
                    annotation?.target.kind === "rendered-element"
                    ? annotation.target.blockId
                    : null;
                const block = blockId && root
                    ? findRenderedBlockRootByBlockId(root, blockId)
                    : null;
                if (block) {
                    block.scrollIntoView?.({ behavior: "smooth", block: "center" });
                    setActiveMark({
                        id: detail.annotationId,
                        rect: block.getBoundingClientRect(),
                    });
                    return;
                }
                if (annotation) gotoAnnotationSource(annotation);
                return;
            }
            mark.scrollIntoView({ behavior: "smooth", block: "center" });
            setActiveMark({
                id: detail.annotationId,
                rect: mark.getBoundingClientRect(),
            });
        };
        window.addEventListener(ANNOTATION_SELECT_EVENT, handler);
        return () => window.removeEventListener(ANNOTATION_SELECT_EVENT, handler);
    }, [annotations, documentPath]);

    useEffect(() => {
        if (!documentPath) return;
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<AnnotationBlockCommentDetail>).detail;
            if (!detail || detail.documentPath !== documentPath) return;
            setPending(null);
            setCommentOpen(false);
            setActiveMark(null);
            setPendingArea(null);
            setAreaPlacement(null);
            setReplyTargetId(null);
            setEditingCommentId(null);
            setPendingBlock({
                blockId: detail.blockId,
                label: detail.label,
                outputHash: detail.outputHash,
                rect: detail.rect,
            });
        };
        window.addEventListener(ANNOTATION_BLOCK_COMMENT_EVENT, handler);
        return () => window.removeEventListener(ANNOTATION_BLOCK_COMMENT_EVENT, handler);
    }, [documentPath]);

    useEffect(() => {
        if (!documentPath) return;
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<AnnotationRebindDetail>).detail;
            if (!detail || detail.documentPath !== documentPath) return;
            setPending(null);
            setCommentOpen(false);
            setPendingBlock(null);
            setPendingArea(null);
            setPendingElement(null);
            setAreaPlacement(null);
            setElementPlacement(null);
            setActiveMark(null);
            setReplyTargetId(null);
            setEditingCommentId(null);
            setRebindingAnnotationId(detail.annotationId);
        };
        window.addEventListener(ANNOTATION_REBIND_EVENT, handler);
        return () => window.removeEventListener(ANNOTATION_REBIND_EVENT, handler);
    }, [documentPath]);

    const rebindAnnotationToSelection = useCallback((annotationId: string, selection: PendingSelection) => {
        if (!documentPath) return;
        const now = new Date().toISOString();
        const context = selection.kind === "source-text"
            ? getSelectionContext(content, selection.startOffset, selection.endOffset)
            : {
                quote: selection.quote,
                prefix: selection.prefix,
                suffix: selection.suffix,
            };
        updateAnnotations(setAnnotations, persist, (current) =>
            current.map((annotation) =>
                annotation.id === annotationId
                    ? {
                        ...annotation,
                        status: annotation.statusBeforeOrphaned ?? "open",
                        statusBeforeOrphaned: undefined,
                        target: selection.kind === "source-text"
                            ? {
                                kind: "source-text",
                                documentPath,
                                range: offsetToSourceRange(
                                    content,
                                    selection.startOffset,
                                    selection.endOffset,
                                ),
                                startOffset: selection.startOffset,
                                endOffset: selection.endOffset,
                                quote: context.quote || selection.quote,
                                prefix: context.prefix,
                                suffix: context.suffix,
                            }
                            : {
                                kind: "rendered-text",
                                documentPath,
                                blockId: selection.blockId,
                                outputHash: selection.outputHash,
                                startOffset: selection.startOffset,
                                endOffset: selection.endOffset,
                                quote: context.quote || selection.quote,
                                prefix: context.prefix,
                                suffix: context.suffix,
                            },
                        updatedAt: now,
                    }
                    : annotation,
            ),
        );
        setRebindingAnnotationId(null);
        setPending(null);
        setActiveMark(null);
        useEditorStore.getState().setStatusMessage(t("annotation.status.reboundToSelection"));
    }, [content, documentPath, persist, t]);

    const handleMouseUp = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        if (!documentPath) return;
        const target = event.target as HTMLElement | null;
        if (target?.closest("[data-md-annotation-root]") !== rootRef.current) return;
        if (target?.closest(".magic-md-annotation-popover")) return;
        window.setTimeout(() => {
            const selection = readSourceSelection(rootRef, content);
            const renderedSelection = selection ?? readRenderedSelection(rootRef);
            if (rebindingAnnotationId && renderedSelection) {
                rebindAnnotationToSelection(rebindingAnnotationId, renderedSelection);
                clearSelection(rootRef);
                return;
            }
            setPending(renderedSelection);
            if (!renderedSelection) {
                setCommentOpen(false);
            }
        }, 0);
    }, [content, documentPath, rebindAnnotationToSelection, rebindingAnnotationId]);

    const handleClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        if (!documentPath) return;
        const target = event.target as HTMLElement | null;
        if (target?.closest("[data-md-annotation-root]") !== rootRef.current) return;
        const mark = target?.closest("[data-md-annotation-ids]") as HTMLElement | null;
        const ids = mark?.getAttribute("data-md-annotation-ids")?.split(/\s+/).filter(Boolean) ?? [];
        const id = ids.find((candidate) =>
            annotations.some((annotation) => annotation.id === candidate),
        );
        if (!id || !mark) {
            const root = rootRef.current;
            if (!root) return;
            const renderedHit = findRenderedTextAnnotationAtPoint({
                rootBoundary: root,
                annotations,
                documentPath,
                clientX: event.clientX,
                clientY: event.clientY,
            });
            if (!renderedHit) return;
            setPending(null);
            setCommentOpen(false);
            setReplyTargetId(null);
            setEditingCommentId(null);
            setActiveMark({
                id: renderedHit.annotation.id,
                rect: renderedHit.rect,
            });
            return;
        }
        setPending(null);
        setCommentOpen(false);
        setReplyTargetId(null);
        setEditingCommentId(null);
        setActiveMark({
            id,
            rect: mark.getBoundingClientRect(),
        });
    }, [annotations, documentPath]);

    const body = (
        <div
            ref={rootRef}
            onClick={handleClick}
            onMouseUp={handleMouseUp}
            className="relative"
            data-md-annotation-root="true"
            data-md-annotation-document-path={documentPath ?? undefined}
        >
            <style>{ANNOTATION_STYLES}</style>
            {children}
            {rebindingAnnotationId && (
                <div
                    className="magic-md-annotation-popover fixed left-1/2 top-4 z-[1000] flex -translate-x-1/2 items-center gap-2 rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg"
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <span className="text-muted-foreground">{t("annotation.rebindPrompt")}</span>
                    <button
                        type="button"
                        className="rounded px-2 py-1 text-muted-foreground hover:bg-muted"
                        onClick={() => setRebindingAnnotationId(null)}
                    >
                        {t("common.cancel")}
                    </button>
                </div>
            )}
            {pending && (
                <div
                    className="magic-md-annotation-popover fixed z-[1000] flex items-center gap-1 rounded-md border border-border bg-popover px-1.5 py-1 text-[11px] shadow-md"
                    style={getAnnotationFloatingStyle(rootRef, pending.rect, { width: 220, height: 42, placement: "above" })}
                    onMouseDown={(event) => event.preventDefault()}
                >
                    <button
                        type="button"
                        className="rounded px-2 py-1 hover:bg-muted"
                        onClick={() => createAnnotation("highlight")}
                    >
                        {t("annotation.style.highlight")}
                    </button>
                    <button
                        type="button"
                        className="rounded px-2 py-1 hover:bg-muted"
                        onClick={() => createAnnotation("underline")}
                    >
                        {t("annotation.style.underline")}
                    </button>
                    <button
                        type="button"
                        className="rounded px-2 py-1 hover:bg-muted"
                        onClick={() => setCommentOpen(true)}
                    >
                        {t("annotation.style.comment")}
                    </button>
                    <button
                        type="button"
                        className="rounded px-2 py-1 text-muted-foreground hover:bg-muted"
                        onClick={() => {
                            setPending(null);
                            setCommentOpen(false);
                            clearSelection(rootRef);
                        }}
                    >
                        {t("common.cancel")}
                    </button>
                </div>
            )}
            {pending && commentOpen && (
                <div
                    className="magic-md-annotation-popover fixed z-[1000] w-80 rounded-md border border-border bg-popover p-2 text-xs shadow-lg"
                    style={getAnnotationFloatingStyle(rootRef, pending.rect, { width: 320, height: 260 })}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <CommentComposerPanel
                        autoFocus
                        tabs={tabs}
                        definitions={definitions}
                        submitLabel={t("common.save")}
                        onCancel={() => setCommentOpen(false)}
                        onSubmit={(draft) => createAnnotation("comment", draft)}
                    />
                </div>
            )}
            {pendingBlock && (
                <div
                    className="magic-md-annotation-popover fixed z-[1000] w-80 rounded-md border border-border bg-popover p-2 text-xs shadow-lg"
                    style={getAnnotationFloatingStyle(rootRef, pendingBlock.rect, { width: 320, height: 280 })}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <div className="mb-2 rounded bg-muted/50 px-2 py-1 text-[11px] text-muted-foreground">
                        {pendingBlock.label ?? pendingBlock.blockId}
                    </div>
                    <CommentComposerPanel
                        autoFocus
                        tabs={tabs}
                        definitions={definitions}
                        placeholder={t("annotation.blockCommentPlaceholder")}
                        submitLabel={t("common.save")}
                        onCancel={() => setPendingBlock(null)}
                        onSubmit={createBlockAnnotation}
                    />
                </div>
            )}
            {pendingArea && (
                <div
                    className="magic-md-annotation-popover fixed z-[1000] w-80 rounded-md border border-border bg-popover p-2 text-xs shadow-lg"
                    style={getAnnotationFloatingStyle(rootRef, pendingArea.rect, { width: 320, height: 280 })}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <div className="mb-2 flex items-center justify-between rounded bg-muted/50 px-2 py-1 text-[11px] text-muted-foreground">
                        <span>{pendingArea.label ?? pendingArea.blockId}</span>
                        <span>
                            {pendingArea.shape === "area"
                                ? t("annotation.areaComment")
                                : t("annotation.pointComment")}
                        </span>
                    </div>
                    <CommentComposerPanel
                        autoFocus
                        tabs={tabs}
                        definitions={definitions}
                        placeholder={t("annotation.areaPointCommentPlaceholder")}
                        submitLabel={t("common.save")}
                        onCancel={() => setPendingArea(null)}
                        onSubmit={createAreaAnnotation}
                    />
                </div>
            )}
            {pendingElement && (
                <div
                    className="magic-md-annotation-popover fixed z-[1000] w-80 rounded-md border border-border bg-popover p-2 text-xs shadow-lg"
                    style={getAnnotationFloatingStyle(rootRef, pendingElement.rect, { width: 320, height: 280 })}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <div className="mb-2 flex items-center justify-between rounded bg-muted/50 px-2 py-1 text-[11px] text-muted-foreground">
                        <span>{pendingElement.label ?? pendingElement.blockId}</span>
                        <span>{pendingElement.elementLabel ?? pendingElement.tagName ?? t("annotation.elementComment")}</span>
                    </div>
                    <CommentComposerPanel
                        autoFocus
                        tabs={tabs}
                        definitions={definitions}
                        placeholder={t("annotation.elementCommentPlaceholder")}
                        submitLabel={t("common.save")}
                        onCancel={() => setPendingElement(null)}
                        onSubmit={createElementAnnotation}
                    />
                </div>
            )}
            {activeMark && activeAnnotation && (
                <div
                    className="magic-md-annotation-popover fixed z-[1000] w-80 rounded-md border border-border bg-popover p-2 text-xs shadow-lg"
                    style={getAnnotationFloatingStyle(rootRef, activeMark.rect, { width: 320, height: 390 })}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    {activeCommentTree.length > 0 ? (
                        <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                            {activeCommentTree.map((node) => (
                                <CommentThreadItem
                                    key={node.comment.id}
                                    node={node}
                                    depth={0}
                                    replyTargetId={replyTargetId}
                                    editingCommentId={editingCommentId}
                                    recentCommentId={recentCommentId}
                                    replyComposerKey={replyComposerKey}
                                    tabs={tabs}
                                    definitions={definitions}
                                    onRecentCommentSettled={() => setRecentCommentId(null)}
                                    onReply={(commentId) => {
                                        setReplyTargetId((current) =>
                                            current === commentId ? null : commentId,
                                        );
                                        setEditingCommentId(null);
                                    }}
                                    onEdit={(commentId) => {
                                        setEditingCommentId(commentId);
                                        setReplyTargetId(null);
                                    }}
                                    onCancelReply={() => setReplyTargetId(null)}
                                    onCancelEdit={() => setEditingCommentId(null)}
                                    onSubmitReply={(commentId, draft) =>
                                        appendComment(activeAnnotation.id, draft, commentId)
                                    }
                                    onSubmitEdit={(commentId, draft) =>
                                        editComment(activeAnnotation.id, commentId, draft)
                                    }
                                    onDelete={(commentId) =>
                                        deleteComment(activeAnnotation.id, commentId)
                                    }
                                />
                            ))}
                        </div>
                    ) : null}
                    <div
                        className={cn(
                            activeCommentTree.length > 0
                                ? "mt-2 border-t border-border/70 pt-2"
                                : "mt-0",
                        )}
                    >
                        {replyTargetId ? (
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-1">
                                    <AnnotationFooterActions
                                        annotation={activeAnnotation}
                                        onGotoSource={() => gotoAnnotationSource(activeAnnotation)}
                                        onResolve={() => resolveAnnotation(activeAnnotation.id)}
                                        onDelete={() => deleteAnnotation(activeAnnotation.id)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="shrink-0 rounded px-2 py-1 text-muted-foreground hover:bg-muted"
                                    onClick={() => setActiveMark(null)}
                                >
                                    {t("common.close")}
                                </button>
                            </div>
                        ) : (
                            <CommentComposerPanel
                                key={`${activeAnnotation.id}:${replyComposerKey}:root`}
                                tabs={tabs}
                                definitions={definitions}
                                placeholder={t("annotation.continueCommentPlaceholder")}
                                submitLabel={t("annotation.style.comment")}
                                cancelLabel={t("common.close")}
                                footerLeading={(
                                    <AnnotationFooterActions
                                        annotation={activeAnnotation}
                                        onGotoSource={() => gotoAnnotationSource(activeAnnotation)}
                                        onResolve={() => resolveAnnotation(activeAnnotation.id)}
                                        onDelete={() => deleteAnnotation(activeAnnotation.id)}
                                    />
                                )}
                                onCancel={() => {
                                    if (editingCommentId) {
                                        setEditingCommentId(null);
                                    } else {
                                        setActiveMark(null);
                                    }
                                }}
                                onSubmit={(draft) =>
                                    appendComment(activeAnnotation.id, draft, null)
                                }
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    if (!value) return body;
    return (
        <AnnotationContext.Provider value={value}>
            {body}
        </AnnotationContext.Provider>
    );
}

function CommentThreadItem({
    node,
    depth,
    replyTargetId,
    editingCommentId,
    recentCommentId,
    replyComposerKey,
    tabs,
    definitions,
    onRecentCommentSettled,
    onReply,
    onEdit,
    onCancelReply,
    onCancelEdit,
    onSubmitReply,
    onSubmitEdit,
    onDelete,
}: {
    node: AnnotationCommentThreadNode;
    depth: number;
    replyTargetId: string | null;
    editingCommentId: string | null;
    recentCommentId: string | null;
    replyComposerKey: number;
    tabs: Array<{ id: string; title: string; filePath?: string }>;
    definitions: Array<{ id: string; pluginName?: string; hasOutput?: boolean }>;
    onRecentCommentSettled: () => void;
    onReply: (commentId: string) => void;
    onEdit: (commentId: string) => void;
    onCancelReply: () => void;
    onCancelEdit: () => void;
    onSubmitReply: (commentId: string, draft: CommentComposerDraft) => void;
    onSubmitEdit: (commentId: string, draft: CommentComposerDraft) => void;
    onDelete: (commentId: string) => void;
}) {
    const t = useT("editor");
    const comment = node.comment;
    const nested = depth > 0;
    const isEditing = editingCommentId === comment.id;
    const isReplying = replyTargetId === comment.id;
    const isRecent = recentCommentId === comment.id;
    const itemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isRecent) return;
        itemRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
        const timeout = window.setTimeout(onRecentCommentSettled, 1200);
        return () => window.clearTimeout(timeout);
    }, [isRecent, onRecentCommentSettled]);

    return (
        <div
            ref={itemRef}
            className={cn(
                "space-y-2",
                nested && "ml-3 border-l border-border/70 pl-2",
            )}
        >
            <div
                className={cn(
                    "group/comment rounded-md border transition-[background-color,border-color,box-shadow]",
                    isRecent && "ring-1 ring-primary/25",
                    isEditing
                        ? "border-primary/25 bg-background shadow-sm"
                        : nested
                            ? "border-transparent bg-muted/25 hover:bg-muted/40"
                            : "border-border/70 bg-background/90 shadow-sm hover:border-border hover:bg-background",
                )}
            >
                {isEditing ? (
                    <div className="p-1.5">
                            <CommentComposerPanel
                                key={`${comment.id}:edit:${replyComposerKey}`}
                                autoFocus
                                variant="compact"
                                initialText={comment.text}
                                initialDocument={comment.document}
                                tabs={tabs}
                                definitions={definitions}
                                submitLabel={t("annotation.saveEdit")}
                                cancelLabel={t("common.cancel")}
                                onCancel={onCancelEdit}
                                onSubmit={(draft) => onSubmitEdit(comment.id, draft)}
                            />
                    </div>
                ) : (
                    <div className="flex items-start gap-2 px-2.5 py-2">
                        <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                        <div className="min-w-0 flex-1">
                            <div className="whitespace-pre-wrap break-words text-[12px] leading-relaxed text-foreground">
                                {comment.text}
                            </div>
                            {comment.references?.length ? (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                    {comment.references.map((reference, index) => (
                                        <span
                                            key={`${comment.id}:${index}`}
                                            className="rounded border border-border/80 bg-muted/35 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
                                        >
                                            {formatCommentReference(reference)}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                            <div className="mt-1.5 text-[10px] text-muted-foreground/70">
                                {formatCommentTime(comment.updatedAt)}
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/comment:opacity-100 group-focus-within/comment:opacity-100">
                            <button
                                type="button"
                                title={t("annotation.reply")}
                                aria-label={t("annotation.replyComment")}
                                className={cn(
                                    "inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                                    isReplying && "bg-primary/10 text-primary",
                                )}
                                onClick={() => onReply(comment.id)}
                            >
                                <Reply size={12} />
                            </button>
                            <button
                                type="button"
                                title={t("annotation.edit")}
                                aria-label={t("annotation.editComment")}
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                onClick={() => onEdit(comment.id)}
                            >
                                <Pencil size={12} />
                            </button>
                            <button
                                type="button"
                                title={t("common.delete")}
                                aria-label={t("annotation.deleteComment")}
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => onDelete(comment.id)}
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </div>
                )}
                {isReplying ? (
                    <div className="border-t border-border/60 p-1.5">
                        <CommentComposerPanel
                            key={`${comment.id}:reply:${replyComposerKey}`}
                            autoFocus
                            variant="compact"
                            tabs={tabs}
                            definitions={definitions}
                            placeholder={t("annotation.replyCommentPlaceholder")}
                            submitLabel={t("annotation.reply")}
                            cancelLabel={t("common.cancel")}
                            onCancel={onCancelReply}
                            onSubmit={(draft) => onSubmitReply(comment.id, draft)}
                        />
                    </div>
                ) : null}
            </div>
            {node.replies.length > 0 ? (
                <div className="space-y-2">
                    {node.replies.map((reply) => (
                        <CommentThreadItem
                            key={reply.comment.id}
                            node={reply}
                            depth={depth + 1}
                            replyTargetId={replyTargetId}
                            editingCommentId={editingCommentId}
                            recentCommentId={recentCommentId}
                            replyComposerKey={replyComposerKey}
                            tabs={tabs}
                            definitions={definitions}
                            onRecentCommentSettled={onRecentCommentSettled}
                            onReply={onReply}
                            onEdit={onEdit}
                            onCancelReply={onCancelReply}
                            onCancelEdit={onCancelEdit}
                            onSubmitReply={onSubmitReply}
                            onSubmitEdit={onSubmitEdit}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function CommentComposerPanel({
    autoFocus,
    variant = "default",
    initialText,
    initialDocument,
    placeholder,
    submitLabel,
    cancelLabel,
    tabs,
    definitions,
    footerLeading,
    onSubmit,
    onCancel,
}: {
    autoFocus?: boolean;
    variant?: "default" | "compact";
    initialText?: string;
    initialDocument?: unknown;
    placeholder?: string;
    submitLabel: string;
    cancelLabel?: string;
    tabs: Array<{ id: string; title: string; filePath?: string }>;
    definitions: Array<{ id: string; pluginName?: string; hasOutput?: boolean }>;
    footerLeading?: ReactNode;
    onSubmit: (draft: CommentComposerDraft) => void;
    onCancel: () => void;
}) {
    const t = useT("editor");
    const [draft, setDraft] = useState<CommentComposerDraft | null>(null);
    const canSubmit = !!draft && hasCommentDraftContent(draft);
    const resolvedCancelLabel = cancelLabel ?? t("common.back");
    const submit = (nextDraft = draft) => {
        if (!nextDraft || !hasCommentDraftContent(nextDraft)) return;
        onSubmit(nextDraft);
    };
    return (
        <>
            <Suspense
                fallback={
                    <div
                        className="min-h-20 rounded border border-border px-3 py-2 text-sm text-muted-foreground"
                        role="status"
                    >
                        {t("common.loading")}
                    </div>
                }
            >
                <CommentComposer
                    autoFocus={autoFocus}
                    variant={variant}
                    initialText={initialText}
                    initialDocument={initialDocument}
                    placeholder={placeholder}
                    tabs={tabs}
                    definitions={definitions}
                    onDraftChange={setDraft}
                    onEscape={onCancel}
                    onSubmit={submit}
                />
            </Suspense>
            <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1">
                    {footerLeading}
                </div>
                <div className="flex shrink-0 justify-end gap-1">
                    <button
                        type="button"
                        className="rounded px-2 py-1 text-muted-foreground hover:bg-muted"
                        onClick={onCancel}
                    >
                        {resolvedCancelLabel}
                    </button>
                    <button
                        type="button"
                        disabled={!canSubmit}
                        className="rounded bg-primary px-2 py-1 text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => submit()}
                    >
                        {submitLabel}
                    </button>
                </div>
            </div>
        </>
    );
}

function AnnotationFooterActions({
    annotation,
    onGotoSource,
    onResolve,
    onDelete,
}: {
    annotation: PreviewAnnotation;
    onGotoSource: () => void;
    onResolve: () => void;
    onDelete: () => void;
}) {
    const t = useT("editor");
    return (
        <>
            {annotation.target.kind === "source-text" ? (
                <button
                    type="button"
                    className="rounded px-2 py-1 text-muted-foreground hover:bg-muted"
                    onClick={onGotoSource}
                >
                    {t("annotation.source")}
                </button>
            ) : null}
            <button
                type="button"
                className="rounded px-2 py-1 text-muted-foreground hover:bg-muted"
                onClick={onResolve}
            >
                {t("annotation.action.resolve")}
            </button>
            <button
                type="button"
                className="rounded px-2 py-1 text-destructive hover:bg-destructive/10"
                onClick={onDelete}
            >
                {t("common.delete")}
            </button>
        </>
    );
}

function updateAnnotations(
    setAnnotations: Dispatch<SetStateAction<PreviewAnnotation[]>>,
    persist: (annotations: PreviewAnnotation[]) => Promise<void>,
    updater: (annotations: PreviewAnnotation[]) => PreviewAnnotation[],
) {
    setAnnotations((current) => {
        const next = updater(current);
        void persist(next);
        return next;
    });
}

function readSourceSelection(
    rootRef: RefObject<HTMLDivElement | null>,
    content: string,
): PendingSelection | null {
    const root = rootRef.current;
    if (!root) return null;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return null;
    const startElement = findSourceElement(range.startContainer);
    const endElement = findSourceElement(range.endContainer);
    if (!startElement || !endElement) return null;
    if (!root.contains(startElement) || !root.contains(endElement)) return null;
    const startOffset = getSourceBoundaryOffset(startElement, range.startContainer, range.startOffset);
    const endOffset = getSourceBoundaryOffset(endElement, range.endContainer, range.endOffset);
    if (startOffset == null || endOffset == null) return null;
    const from = Math.max(0, Math.min(startOffset, endOffset, content.length));
    const to = Math.max(0, Math.min(Math.max(startOffset, endOffset), content.length));
    if (to <= from) return null;
    return {
        kind: "source-text",
        startOffset: from,
        endOffset: to,
        quote: selection.toString(),
        rect: typeof range.getBoundingClientRect === "function"
            ? range.getBoundingClientRect()
            : startElement.getBoundingClientRect(),
    };
}

function readRenderedSelection(
    rootRef: RefObject<HTMLDivElement | null>,
): PendingSelection | null {
    const root = rootRef.current;
    if (!root) return null;
    const selection = readRenderedTextSelection(root);
    if (!selection) return null;
    return {
        kind: "rendered-text",
        blockId: selection.blockId,
        outputHash: selection.outputHash,
        startOffset: selection.startOffset,
        endOffset: selection.endOffset,
        quote: selection.quote,
        prefix: selection.prefix,
        suffix: selection.suffix,
        rect: selection.rect,
    };
}

function buildRenderedAnnotationIndex(
    annotations: PreviewAnnotation[],
    documentPath: string | null | undefined,
): RenderedAnnotationIndex {
    const block = new Map<string, PreviewAnnotation[]>();
    const area = new Map<string, PreviewAnnotation[]>();
    if (!documentPath || annotations.length === 0) return { block, area };

    for (const annotation of annotations) {
        if (annotation.status !== "open") continue;
        const target = annotation.target;
        if (target.documentPath !== documentPath) continue;
        if (
            target.kind !== "rendered-block" &&
            target.kind !== "rendered-area" &&
            target.kind !== "rendered-element"
        ) {
            continue;
        }

        pushAnnotation(block, target.blockId, annotation);
        if (target.kind === "rendered-area" || target.kind === "rendered-element") {
            pushAnnotation(area, target.blockId, annotation);
        }
    }

    return { block, area };
}

function pushAnnotation(
    target: Map<string, PreviewAnnotation[]>,
    blockId: string,
    annotation: PreviewAnnotation,
) {
    const list = target.get(blockId);
    if (list) {
        list.push(annotation);
        return;
    }
    target.set(blockId, [annotation]);
}

function findSourceElement(node: Node): HTMLElement | null {
    const start = node.nodeType === Node.ELEMENT_NODE
        ? (node as HTMLElement)
        : node.parentElement;
    return start?.closest("[data-md-source-start][data-md-source-end]") ?? null;
}

function getSourceBoundaryOffset(
    sourceElement: HTMLElement,
    container: Node,
    offset: number,
): number | null {
    const base = Number(sourceElement.dataset.mdSourceStart);
    if (!Number.isFinite(base)) return null;
    const ownerDocument = sourceElement.ownerDocument;
    const range = ownerDocument.createRange();
    range.selectNodeContents(sourceElement);
    try {
        range.setEnd(container, offset);
    } catch {
        return null;
    }
    return base + range.toString().length;
}

function clearSelection(rootRef: RefObject<HTMLDivElement | null>) {
    const selection = rootRef.current?.ownerDocument.defaultView?.getSelection();
    selection?.removeAllRanges();
}

function createAnnotationId(): string {
    const random = globalThis.crypto?.randomUUID?.();
    if (random) return `ann_${random}`;
    return `ann_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function createCommentId(): string {
    const random = globalThis.crypto?.randomUUID?.();
    if (random) return `cmt_${random}`;
    return `cmt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function createThreadComment(
    draft: CommentComposerDraft,
    timestamp: string,
    parentId?: string,
): AnnotationComment {
    return {
        id: createCommentId(),
        parentId,
        text: draft.text,
        document: draft.document,
        references: draft.references,
        createdAt: timestamp,
        updatedAt: timestamp,
    };
}

function formatCommentReference(reference: AnnotationCommentReference): string {
    const prefix = reference.kind === "tag" ? "#" : reference.kind === "command" ? "/" : "@";
    return `${prefix}${reference.label}`;
}

function gotoAnnotationSource(annotation: PreviewAnnotation) {
    const target = annotation.target;
    if (target.kind !== "source-text") return;
    window.dispatchEvent(
        new CustomEvent("magic-md:goto-line", {
            detail: {
                line: target.range.startLine,
                col: target.range.startColumn + 1,
            },
        }),
    );
}

function cssAttributeValue(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function formatCommentTime(iso: string): string {
    const timestamp = Date.parse(iso);
    if (Number.isNaN(timestamp)) return iso;
    return new Date(timestamp).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

const ANNOTATION_STYLES = `
.magic-md-annotation-source {
  scroll-margin-top: 5rem;
}
.magic-md-annotation-mark {
  border-radius: 0.18rem;
  cursor: pointer;
}
.magic-md-annotation-highlight {
  background: color-mix(in srgb, #facc15 34%, transparent);
}
.magic-md-annotation-underline {
  text-decoration-line: underline;
  text-decoration-style: wavy;
  text-decoration-color: color-mix(in srgb, #2563eb 72%, transparent);
  text-decoration-thickness: 1.35px;
  text-underline-offset: 0.28em;
  text-decoration-skip-ink: auto;
}
.magic-md-annotation-comment {
  margin-inline: 0.02em;
  padding: 0.06em 0.12em 0.08em;
  border-radius: 0.22em;
  background-color: color-mix(in srgb, #14b8a6 9%, transparent);
  background-image: radial-gradient(
    circle,
    color-mix(in srgb, #0f766e 46%, transparent) 0 0.9px,
    transparent 1px
  );
  background-repeat: repeat-x;
  background-size: 6px 2px;
  background-position: 0.12em calc(100% - 0.12em);
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}
.magic-md-annotation-underline.magic-md-annotation-comment {
  text-underline-offset: 0.38em;
}
.magic-md-rendered-text-scope {
  display: contents;
}
.magic-md-rendered-text-scope-inline {
  display: inline;
}
.magic-md-comment-composer-editor p {
  margin: 0;
}
.magic-md-annotation-comment-ref {
  display: inline-flex;
  max-width: 180px;
  align-items: center;
  border-radius: 0.25rem;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--muted));
  padding: 0 0.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 10px;
  color: hsl(var(--muted-foreground));
  vertical-align: baseline;
}
`;
